import * as THREE from 'three'
import ReactDOM from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { SimpleReverb } from './SimpleReverb'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import './styles.css'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let bg = "#000"

let colDec = randI(200) * 0.01
let peq = 0.06
let melodyLen = 4
let melodySteps = new Array(melodyLen).fill().map(() => (14 + randI(26)) * 50)
let peqs = new Array(melodyLen).fill().map(() => peq + (randI(49) * 0.001 * rand([1, 1.1, 1.25, 1.3, 1.5, -1])))
let lerpSpeeds = new Array(melodyLen).fill().map(() => 0.12 - (randI(11) * 0.01 * rand([1, -0.8])))
let lerpMult = 0.1
let sqrrrt = 3 + (randI(90) * 0.1)
let detail = 6 + randI(rand([10,10,10,20,20, 30, 30, 40, 40, 40, 50, 60, 70, 80]))
let colMult = 2 + (randI(50) * 0.01)
let toType = rand([0, 0, 0, 0, 0, 0, 0, 0, 1])
let bitCheck = rand([2, 2, 2, 3, 4, 1 + randI(30)])
let smoothing = 2 + (randI(200) * 0.01)
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.uniforms.time = { value: 0 }
    this.shaderMaterial.uniforms.colMult = { value: colMult }
    this.shaderMaterial.uniforms.colDec = { value: colDec }
    this.shaderMaterial.uniforms.peq = { value: peq }
    this.shaderMaterial.uniforms.sqrrrt = { value: sqrrrt }
    this.shaderMaterial.uniforms.detail = { value: detail }
    this.shaderMaterial.uniforms.toType = { value: toType }
    this.shaderMaterial.uniforms.bitCheck = { value: bitCheck }
    this.shaderMaterial.uniforms.smoothing = { value: smoothing }
    this.shaderMaterial.uniforms.colPush = { value: wild ? 0.25 : 0 }
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
uniform float time;
uniform float colDec;
uniform float peq;
uniform float sqrrrt;
uniform float colMult;
uniform float smoothing;
uniform int detail;
uniform float colPush;
uniform int toType;
uniform uint bitCheck;
varying vec2 vUv;

uint ihash(uint x) {
  x += 0x12345678u;
    x ^= x >> 16;
    x *= 0x7feb352dU;
    x ^= x >> 15;
    x *= 0x846ca68bU;
    x ^= x >> 16;
    return x;
}

vec3 h2rgb(float h) {
  vec3 rgb = clamp( abs(mod(h*6.0+vec3(0.0,4.0,2.0),colMult)-3.0)-1.0, 0.0, 1.0 );
  return rgb*rgb*(4.0-smoothing*rgb); // cubic smoothing	
}

float rectdist(vec2 p, vec2 a, vec2 b) {
  return max(max(a.y-p.y,p.y-b.y),
             max(a.x-p.x,p.x-b.x));
}
vec4 when_gt( vec4 x, float y ) {
    return max( sign( x - y ), 0.000000001 );
}

void main() {
  float phi = peq*(sqrt(sqrrrt)-1.5); 
  vec2 p = (-1.0 + 2.0 *vUv);
  float px = fwidth(p.x);
  vec3 col = vec3(1,1,1);
  vec2 a = vec2(-1.0-phi,-1);
  vec2 b = vec2(1.0+phi,1);
  int type = 0;
  uint h = uint(time*1000.0);
  float d = rectdist(p,a,b);
  if (d < 0.0) {
    int N = detail;
    for (int i = 0; 
         i < N; 
         i++, h = ihash(h^uint(type))) {
      if (type == 0) {
        bool flip = bool(h & bitCheck);
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = (1.0-phi)*a.x + phi*b.x;
          if (p.x < x1) { 
            b = vec2(x1,b.y); 
            type = 1; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = 2; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      } else {
        bool flip = b.y-a.y > b.x-a.x;
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = a.x+b.y-a.y;
          if (p.x < x1) { 
            b = vec2(x1,b.y); 
            type = 0; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = toType; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      }
    }
    col = h2rgb(colDec+colPush+(float(h)*0.6)/exp2(32.0));
  }
  gl_FragColor = vec4(col,0.01);
}

`
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })
let c = 0
let mc = 0
let pi = 0
let pt = peqs[0]
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    c++

    reverb.cutoff.value = 220 + (Math.cos(c*0.003) * 160)
    if (wild) {
      filters.forEach((f, i) => {
        f.frequency.value = (pt*0.3) * freqs[i] * 150
      })
      if (mc === melodySteps[pi]) {
        mc = 0
        pi = (pi + 1) % melodyLen
      } else {
        mc++
      }
      ref.current.uniforms.peq.value = THREE.MathUtils.lerp(ref.current.uniforms.peq.value, peqs[pi], lerpSpeeds[pi] * 0.2*lerpMult)
    }
    ref.current.uniforms.colMult.value = (wild || !audioStarted) ? colMult : 2.02

    lerpMult = THREE.MathUtils.lerp(lerpMult, 1, 0.04)
    pt = THREE.MathUtils.lerp(pt, peqs[pi], 0.8)
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}
let wild = false
function App() {
  const size = useWindowSize();
  const start = () => {
    if (!audioStarted) {
      whiteNoise.start(0);
      audioStarted = true;
      fgains.forEach((g) => {
        g.gain.linearRampToValueAtTime(4, audioContext.currentTime + (2))
      })
    }
    wild = !wild
    if(wild){
      pi = (pi+1)%melodyLen;
    }
  }
  return (
    <div id="main" onClick={() => start()}
      onDoubleClick={() => {
        toggleFullScreen()
      }}
      key={size.width + size.height}
    >
      <Canvas flat={false} linear={false} dpr={[1, 2]} camera={{ position: [0, 0, 2], near: 0.0001, far: 1000, fov: 35 }}
        gl={{ alpha: false, antialias: false, stencil: false, depth: false }}
      >
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Tint />
      </Canvas>
    </div>
  )
}

ReactDOM.render(
  <App />,
  document.getElementById('root')
)

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext = new AudioContext();
let audioStarted = false;
let bufferSize = 8 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
for (let i = 0; i < bufferSize; i++) {
  output[i] = Math.random() * 2 - 1;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 8
reverb.filterType = 'lowpass'
reverb.cutoff.value = 900
reverb.wet.value = 0.8
reverb.dry.value = (2+randI(10))*0.001

var getFrequency = function (note) {
  var notes = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'],
    octave,
    keyNumber;
  if (note.length === 3) {
    octave = note.charAt(2);
  } else {
    octave = note.charAt(1);
  }
  keyNumber = notes.indexOf(note.slice(0, -1));
  if (keyNumber < 3) {
    keyNumber = keyNumber + 12 + ((octave - 1) * 12) + 1;
  } else {
    keyNumber = keyNumber + ((octave - 1) * 12) + 1;
  }
  return 440 * Math.pow(2, (keyNumber - 49) / 12);
};

let NUM = 7;
let filters = [];
let fgains = [];
let panners = [];

let freqs = []

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  let p = audioContext.createStereoPanner();
  p.pan.value = (randI(20) * 0.1) - 1;
  panners.push(p)
  f.type = "bandpass";
  f.Q.value = 400;
  let fr = getFrequency(rand(['C', 'D', 'E', 'F', 'G', 'A', 'B']) + rand([2, 2, 2, 3, 3, 4 ]))
  freqs.push(fr)
  f.frequency.value = fr
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}

for (let i = 0; i < NUM; i++) {
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(panners[i]);
  panners[i].connect(reverb);
}


function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else if (document.exitFullscreen) {
    // document.exitFullscreen();
  }
}
function useWindowSize() { let [e, i] = useState({ width: void 0, height: void 0 }); return useEffect(() => { function e() { i({ width: window.innerWidth, height: window.innerHeight }) } return window.addEventListener("resize", e), e(), () => window.removeEventListener("resize", e) }, []), e }

console.log('%c 🐞🐞 @ramioso 2023-02-14 🐞🐞', 'color: blue');
